import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  FireIcon, GlobeHemisphereWestIcon, TrendUpIcon, WarningCircleIcon,
  ArrowSquareOutIcon, SpinnerGapIcon, MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import { fetchTrendingThemes, fetchTrendingWorld } from "../lib/api";
import { fmtUsd, scoreColor } from "../lib/format";

const HOT = "#FF7A1A";

const fmt = (n) => (n == null ? "—" : fmtUsd(n));

export default function Trending() {
  const themesQ = useQuery({ queryKey: ["trendingThemes"], queryFn: fetchTrendingThemes, refetchInterval: 120000 });
  const worldQ = useQuery({ queryKey: ["trendingWorld"], queryFn: fetchTrendingWorld, refetchInterval: 600000 });

  const themes = themesQ.data?.themes || [];
  const trends = worldQ.data?.trends || [];

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="trending-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter flex items-center gap-2.5">
            <FireIcon size={32} weight="fill" style={{ color: HOT }} className="animate-pulse" /> Trending
          </h1>
          <span className="font-mono text-[10px] px-2 py-1 border animate-pulse-hot" style={{ color: HOT, borderColor: HOT + "66" }}>
            LIVE · NARRATIVE RADAR
          </span>
        </div>
        <p className="text-muted-foreground mt-2 text-sm max-w-3xl leading-relaxed">
          The day's hot narratives — the meme themes clustering on-chain right now, plus what's trending in the world
          and which tokens are riding it. Themes move markets: a movie, an Elon tweet, a Grok name, a viral moment can
          become the day's runner.
        </p>
      </div>

      {/* DYOR disclaimer */}
      <div className="border p-4 flex items-start gap-3" style={{ borderColor: HOT + "44", background: HOT + "0D" }} data-testid="trending-disclaimer">
        <WarningCircleIcon size={20} weight="fill" style={{ color: HOT }} className="shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="font-semibold text-white">Just a curiosity radar — not financial advice.</span> Trending topics and
          on-chain name-clusters do <span className="font-semibold">not</span> mean a token exists, is legit, or will pump — most meme
          tokens go to zero and many are scams. Nothing here is a recommendation to buy. Always do your own research (DYOR), verify
          contracts, and never invest more than you can lose.
        </p>
      </div>

      {/* Section 1: Hot meme themes on-chain */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendUpIcon size={22} weight="bold" style={{ color: HOT }} />
          <h2 className="font-heading font-bold text-xl">Hot Meme Themes <span className="text-muted-foreground text-sm font-body">· on-chain right now</span></h2>
        </div>
        {themesQ.isLoading ? (
          <div className="flex justify-center py-12"><SpinnerGapIcon size={26} className="animate-spin" style={{ color: HOT }} /></div>
        ) : themes.length === 0 ? (
          <p className="text-sm text-muted-foreground font-mono py-8 text-center border border-white/10 bg-card" data-testid="themes-empty">
            No strong name-clusters forming this moment — check back soon, narratives spin up fast.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hot-themes-list">
            {themes.map((t) => (
              <div key={t.theme} data-testid={`theme-${t.theme}`} className="border border-white/10 bg-card p-4 hover:border-white/20 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-heading font-bold text-lg capitalize truncate">"{t.theme}"</span>
                  {t.rising && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 flex items-center gap-1 shrink-0" style={{ color: HOT, background: HOT + "1A", border: `1px solid ${HOT}55` }}>
                      <TrendUpIcon size={10} weight="bold" /> RISING
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  <span className="font-mono font-bold text-white">{t.count}</span> tokens launching around this narrative
                </p>
                {t.topToken && (
                  <Link to={`/coin/${t.topToken.mint}`} data-testid={`theme-token-${t.theme}`} className="mt-3 flex items-center gap-2.5 border border-white/10 bg-black/30 p-2 hover:border-neon/40 transition-colors">
                    {t.topToken.image ? (
                      <img src={t.topToken.image} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                    ) : (
                      <div className="w-8 h-8 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-[10px] shrink-0">{(t.topToken.symbol || "?").slice(0, 3)}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-sm truncate">${(t.topToken.symbol || "?").replace("$", "")}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{fmt(t.topToken.marketCap)} mcap</div>
                    </div>
                    {t.topToken.score != null && (
                      <span className="font-mono font-bold text-sm shrink-0" style={{ color: scoreColor(t.topToken.score) }}>{t.topToken.score}</span>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Section 2: Trending in the world */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <GlobeHemisphereWestIcon size={22} weight="bold" style={{ color: HOT }} />
          <h2 className="font-heading font-bold text-xl">Trending in the World <span className="text-muted-foreground text-sm font-body">· worldwide → tokens</span></h2>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Live worldwide search trends (via Google Trends). A ✅ means a token already matches that trend — a 🔎 means no token yet (a potential gap).
        </p>
        {worldQ.isLoading ? (
          <div className="flex flex-col items-center py-12 gap-2">
            <SpinnerGapIcon size={26} className="animate-spin" style={{ color: HOT }} />
            <span className="text-xs font-mono text-muted-foreground">Scanning global trends…</span>
          </div>
        ) : trends.length === 0 ? (
          <p className="text-sm text-muted-foreground font-mono py-8 text-center border border-white/10 bg-card" data-testid="world-empty">
            Couldn't load world trends right now — try again shortly.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="world-trends-list">
            {trends.map((r, i) => (
              <div key={i} data-testid={`world-trend-${i}`} className={`border p-3.5 flex items-center gap-3 ${r.hasToken ? "border-neon/25 bg-neon/[0.03]" : "border-white/10 bg-card"}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold capitalize truncate">{r.term}</span>
                  </div>
                  {r.hasToken ? (
                    <a href={r.token.url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-mono text-neon hover:underline">
                      ${(r.token.symbol || "?").replace("$", "")} · {fmt(r.token.marketCap)} <ArrowSquareOutIcon size={11} />
                    </a>
                  ) : (
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
                      <MagnifyingGlassIcon size={11} /> no token yet
                    </span>
                  )}
                </div>
                <span className="text-lg shrink-0">{r.hasToken ? "✅" : "🔎"}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="text-[11px] text-muted-foreground text-center pt-2">
        Themes = on-chain name-clusters (DexScreener/GeckoTerminal). World trends = Google Trends (worldwide). Not affiliated with X/Twitter. Not financial advice — DYOR.
      </p>
    </div>
  );
}
